/**
 * NOS-262: Table-parameterized CRUD factory for org-scoped media taxonomies
 * (topics / speakers / series). One implementation, three instantiations —
 * see useMediaTopics.ts, useMediaSpeakers.ts, useMediaSeries.ts.
 *
 * Every mutation resolves to { ok, error?, row? } — errors are messages fit
 * for a toast, with unique-constraint (23505) collisions mapped to the
 * config's duplicateMessage.
 */
import { useCallback, useEffect, useState } from "react"
import type { SupabaseClient } from "@supabase/supabase-js"
import { supabase } from "@/lib/supabase"

// Table names arrive as config strings, which the schema-typed client
// rejects; queries run through this untyped view and row typing is restored
// by the Row generic on the returned hook.
const db = supabase as SupabaseClient

export type TaxonomyResult<Row> = { ok: boolean; error?: string; row?: Row }

type CountConfig =
  | {
      /** Count rows in a junction table matching the taxonomy id (topics/speakers). */
      via: "junction"
      table: string
      fk: string
    }
  | {
      /** Count org rows in a table holding a direct FK column (series ← media_videos). */
      via: "orgColumn"
      table: string
      fk: string
    }

export interface OrgTaxonomyConfig<Insert> {
  table: string
  orderBy: string
  /** Toast message when an insert/update hits a unique constraint (23505). */
  duplicateMessage: string
  /** When set, list rows are augmented with `video_count`. */
  count?: CountConfig
  /** Blocks delete while references exist; the two shapes word their errors differently. */
  deleteGuard: {
    table: string
    fk: string
    selectColumn: string
    scopeToOrg?: boolean
    message: string
  }
  /** Normalizes payloads (trimming, slug defaults) before insert/update. */
  prepare: (values: Insert) => Insert
}

function mutationError(
  error: { code?: string; message: string } | null,
  duplicateMessage: string
): string {
  if (!error) return "Unknown error"
  return error.code === "23505" ? duplicateMessage : error.message
}

export function makeOrgTaxonomyHook<Row extends { id: string }, Insert extends object>(
  config: OrgTaxonomyConfig<Insert>
) {
  return function useTaxonomy(orgId: string | null) {
    const [rows, setRows] = useState<Row[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const refresh = useCallback(async () => {
      if (!orgId) {
        setRows([])
        setLoading(false)
        return
      }
      setLoading(true)
      setError(null)
      const { data, error: fetchError } = await db
        .from(config.table)
        .select("*")
        .eq("org_id", orgId)
        .order(config.orderBy)

      if (fetchError) {
        setError(fetchError.message)
        setRows([])
        setLoading(false)
        return
      }

      let result = (data ?? []) as Row[]
      if (config.count && result.length > 0) {
        const countMap: Record<string, number> = {}
        result.forEach((r) => (countMap[r.id] = 0))
        const { fk } = config.count
        const refRows =
          config.count.via === "junction"
            ? (
                await db
                  .from(config.count.table)
                  .select(fk)
                  .in(
                    fk,
                    result.map((r) => r.id)
                  )
              ).data
            : (
                await db
                  .from(config.count.table)
                  .select(fk)
                  .eq("org_id", orgId)
                  .not(fk, "is", null)
              ).data
        for (const ref of (refRows ?? []) as unknown as Record<string, string | null>[]) {
          const id = ref[fk]
          if (id && countMap[id] !== undefined) countMap[id]++
        }
        result = result.map((r) => ({ ...r, video_count: countMap[r.id] ?? 0 }))
      }
      setRows(result)
      setLoading(false)
    }, [orgId])

    useEffect(() => {
      refresh()
    }, [refresh])

    const create = useCallback(
      async (values: Insert): Promise<TaxonomyResult<Row>> => {
        if (!orgId) return { ok: false, error: "No organization selected" }
        const { data, error: insertError } = await db
          .from(config.table)
          .insert({ ...config.prepare(values), org_id: orgId })
          .select()
          .single()
        if (insertError || !data) {
          return { ok: false, error: mutationError(insertError, config.duplicateMessage) }
        }
        return { ok: true, row: data as Row }
      },
      [orgId]
    )

    const update = useCallback(
      async (id: string, values: Insert): Promise<TaxonomyResult<Row>> => {
        if (!orgId) return { ok: false, error: "No organization selected" }
        const { error: updateError } = await db
          .from(config.table)
          .update(config.prepare(values))
          .eq("id", id)
          .eq("org_id", orgId)
        if (updateError) {
          return { ok: false, error: mutationError(updateError, config.duplicateMessage) }
        }
        return { ok: true }
      },
      [orgId]
    )

    const remove = useCallback(
      async (id: string): Promise<TaxonomyResult<Row>> => {
        if (!orgId) return { ok: false, error: "No organization selected" }
        const guard = config.deleteGuard
        let countQuery = db
          .from(guard.table)
          .select(guard.selectColumn, { count: "exact", head: true })
          .eq(guard.fk, id)
        if (guard.scopeToOrg) countQuery = countQuery.eq("org_id", orgId)
        const { count, error: countError } = await countQuery
        if (countError) return { ok: false, error: countError.message }
        if (count && count > 0) return { ok: false, error: guard.message }

        const { error: deleteError } = await db
          .from(config.table)
          .delete()
          .eq("id", id)
          .eq("org_id", orgId)
        if (deleteError) return { ok: false, error: deleteError.message }
        return { ok: true }
      },
      [orgId]
    )

    return { rows, loading, error, refresh, create, update, remove }
  }
}
